import { DefinitionLevels, SettingValues } from '@linearb/linjs-common';
import useSettingsService from 'components/hooks/useSettingsService';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { teamsSelector } from 'state/selectors/teams';
import { selectedTeamSelector } from 'state/selectors/ui-state';
import { PM_METRICS_DEFINITION } from '../../../../../components/usePMConfig';
import _ from 'lodash';
import { TEAM_TYPES } from 'common/constants/constants';

// custom hook to handle parent Incidents settings
export const useParentConfiguration = () => {
  const selectedTeam = useSelector(selectedTeamSelector) || {};
  const allTeams = useSelector(teamsSelector) || [];
  const { getSettingOrDefinitionValue } = useSettingsService();

  const [parentCFRConfig, setParentCFRConfig] = useState({});
  const [parentMTTRConfig, setParentMTTRConfig] = useState({});
  const [parentIncidentsStrategy, setParentIncidentsStrategy] = useState({});

  // get the first parent without use_parent key in SettingValues.INCIDENTS_STRATEGY
  // returns null when the first parent with config is the ORG
  const getFirstParentWithConfiguration = () => {
    let currentId = selectedTeam.parent_id;
    let currentIncidentStrategy =
      getSettingOrDefinitionValue({
        name: SettingValues.INCIDENTS_STRATEGY,
        definitionLevel: DefinitionLevels.TEAM,
        teamId: currentId
      }) || {};

    while (currentId && currentIncidentStrategy.use_parent) {
      const currentTeam = _.find(allTeams, team => team.id === currentId);
      const currentParentId = currentTeam?.parent_id;
      const definitionLevel = !!currentParentId
        ? DefinitionLevels.TEAM
        : DefinitionLevels.ORGANIZATION;
      currentIncidentStrategy = getSettingOrDefinitionValue({
        name: SettingValues.INCIDENTS_STRATEGY,
        definitionLevel,
        ...(currentId && { teamId: currentParentId })
      });
      currentId = currentParentId;
    }
    return currentId;
  };

  const getParentConfiguration = () => {
    const parentId = getFirstParentWithConfiguration();
    const parent = _.find(allTeams, team => team.id === parentId);
    const isOrgLevel = parent?.type === TEAM_TYPES.ALL_TEAMS || !parentId;
    const definitionLevel = isOrgLevel
      ? DefinitionLevels.ORGANIZATION
      : DefinitionLevels.TEAM;

    const parentCFRConfig = getSettingOrDefinitionValue({
      name: PM_METRICS_DEFINITION.CFR.settings_value,
      definitionLevel,
      ...(!isOrgLevel && { teamId: parentId })
    });

    const parentMTTRConfig = getSettingOrDefinitionValue({
      name: PM_METRICS_DEFINITION.MTTR.settings_value,
      definitionLevel,
      ...(!isOrgLevel && { teamId: parentId })
    });

    const parentIncidentsStrategy = getSettingOrDefinitionValue({
      name: SettingValues.INCIDENTS_STRATEGY,
      definitionLevel,
      ...(!isOrgLevel && { teamId: parentId })
    });
    return { parentCFRConfig, parentMTTRConfig, parentIncidentsStrategy };
  };

  // init selected filters from configuration
  useEffect(() => {
    const { parentCFRConfig, parentMTTRConfig, parentIncidentsStrategy } =
      getParentConfiguration();
    setParentCFRConfig(parentCFRConfig);
    setParentMTTRConfig(parentMTTRConfig);
    setParentIncidentsStrategy(parentIncidentsStrategy);
  }, []);

  const getParentName = () => {
    const parentId = selectedTeam?.parent_id;
    const parent = _.find(allTeams, team => team.id === parentId);
    return parent?.name || 'Organization';
  };

  return {
    getParentName,
    parentIncidentsStrategy,
    getFirstParentWithConfiguration,
    parentCFRConfig,
    parentMTTRConfig
  };
};
